export function isValidTimezone(value: unknown): value is string {
  if (typeof value !== "string" || !value.trim() || value !== value.trim() || /^[+-]/.test(value)) return false;
  try {
    new Intl.DateTimeFormat("en", { timeZone: value });
    return true;
  } catch (error) {
    if (error instanceof RangeError) return false;
    throw error;
  }
}

// Picker zones, east to west. Each covers IANA zones for the 50 U.S. states and
// Washington, D.C. that follow the same rules today; offsets alone do not
// distinguish U.S. locations or their daylight-saving rules.
const usTimezoneGroups = [
  {
    value: "America/New_York",
    label: "Eastern Time (ET)",
    members: [
      "America/Detroit",
      "America/Indiana/Indianapolis",
      "America/Indiana/Marengo",
      "America/Indiana/Petersburg",
      "America/Indiana/Vevay",
      "America/Indiana/Vincennes",
      "America/Indiana/Winamac",
      "America/Kentucky/Louisville",
      "America/Kentucky/Monticello",
    ],
  },
  {
    value: "America/Chicago",
    label: "Central Time (CT)",
    members: [
      "America/Indiana/Knox",
      "America/Indiana/Tell_City",
      "America/Menominee",
      "America/North_Dakota/Beulah",
      "America/North_Dakota/Center",
      "America/North_Dakota/New_Salem",
    ],
  },
  { value: "America/Denver", label: "Mountain Time (MT)", members: ["America/Boise"] },
  { value: "America/Phoenix", label: "Mountain Time – Arizona (no DST)", members: [] },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)", members: [] },
  {
    value: "America/Anchorage",
    label: "Alaska Time (AKT)",
    members: ["America/Juneau", "America/Metlakatla", "America/Nome", "America/Sitka", "America/Yakutat"],
  },
  { value: "America/Adak", label: "Hawaii-Aleutian Time – Aleutian Islands (DST)", members: [] },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST, no DST)", members: [] },
];

const canonicalTimezone = (timeZone: string) =>
  new Intl.DateTimeFormat("en", { timeZone }).resolvedOptions().timeZone;

const groupByCanonicalTimezone = new Map(usTimezoneGroups.flatMap((group) =>
  [group.value, ...group.members].map((timeZone) => [canonicalTimezone(timeZone), group] as const),
));

function timezoneGroup(value: unknown) {
  return isValidTimezone(value) ? groupByCanonicalTimezone.get(canonicalTimezone(value)) : undefined;
}

export function isUsTimezone(value: unknown): value is string {
  return timezoneGroup(value) !== undefined;
}

export function timezoneLabel(value: string) {
  const group = timezoneGroup(value);
  if (!group) return value;
  if (value === group.value) return group.label;
  const place = /^(America|Pacific)\//.test(value) ? value.split("/").pop()!.replaceAll("_", " ") : value;
  return `${group.label} – ${place}`;
}

export function timezoneOptions(current: string) {
  return [...new Set([...usTimezoneGroups.map((group) => group.value), ...(isUsTimezone(current) ? [current] : [])])];
}

export function defaultBusinessTimezone() {
  return timezoneGroup(Intl.DateTimeFormat().resolvedOptions().timeZone)?.value ?? "";
}
